import { useEffect, useState } from 'react';
import { Text, Button } from '@janeapp/burrito-design-system';
import { LoadingView } from '~/components';
import {
  PractitionersSearchResult,
  PractitionersApi,
  PractitionerOpening,
} from '~/api';
import { buildPractitionerBookingUrlWithTreatmentId } from '~/utilities';
import { trackEvent } from '~/hooks';
import { getBaseUrl } from '~/env';
import { Opening } from './Opening';

interface OpeningsProps {
  result: PractitionersSearchResult;
}

export const Openings = ({ result }: OpeningsProps) => {
  const [openings, setOpenings] = useState<PractitionerOpening[]>([]);
  const [openingsLoading, setOpeningsLoading] = useState(false);
  const [openingsError, setOpeningsError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOpenings = async () => {
      setOpeningsLoading(true);
      setOpeningsError(null);

      try {
        const api = new PractitionersApi({ baseUrl: getBaseUrl() });

        // Extract locationId from marketplacePractitionerLocationId (format: "xxx-xx_ID")
        const locationId = Number(
          result.marketplacePractitionerLocationId.split('_').pop()
        );

        const response = await api.getOpenings(
          result.staffMemberGuid,
          locationId
        );
        const data = await response.json();
        setOpenings(data.openings || []);
      } catch (error) {
        setOpeningsError('Failed to load availability');
      } finally {
        setOpeningsLoading(false);
      }
    };

    fetchOpenings();
  }, [result.staffMemberGuid, result.marketplacePractitionerLocationId]);

  const handleOpeningClick = () => {
    if (!result.clinicBookingUrl) return;
    if (!result.defaultTreatmentId) return;

    trackEvent('mpl_view_booking_site_button_clicked', {
      name: result.fullName,
      clinic_name: result.clinicName,
      url: result.clinicBookingUrl,
    });

    const bookingUrl = buildPractitionerBookingUrlWithTreatmentId(
      result.staffMemberGuid,
      result.clinicBookingUrl,
      result.defaultTreatmentId
    );

    window.open(bookingUrl, '_blank');
  };

  const upcomingOpenings = openings.slice(0, 5);

  if (openingsLoading) {
    return (
      <div className="mt-4 flex items-center justify-center">
        <LoadingView loading={true} message={''} size="2xl" />
      </div>
    );
  }

  if (openingsError) {
    return (
      <div className="mt-4">
        <Text size="sm" className="italic text-neutral-60">
          {openingsError}
        </Text>
      </div>
    );
  }

  if (upcomingOpenings.length === 0) {
    return (
      <div className="mt-4">
        <Text size="md" className="text-bold-90 mb-2 font-bold">
          Next available times with {result.firstName}
        </Text>
        <div className="rounded-md border border-neutral-30 bg-neutral-10 p-4 text-center">
          <Text size="sm" className="text-neutral-60">
            This practitioner doesn&apos;t have any availability in the next 7
            days.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <Text size="md" className="text-bold-90 mb-2 font-bold">
        Next available times with {result.firstName}
      </Text>
      <div className="flex flex-col gap-2.5">
        {upcomingOpenings.map((opening) => (
          <Opening
            key={`${opening.start}-${opening.end}`}
            opening={opening}
            onBookingClick={handleOpeningClick}
          />
        ))}
      </div>
      <Button className="mt-2 w-full text-md" onClick={handleOpeningClick}>
        See more times
      </Button>
    </div>
  );
};
